const { MatcherCategoryFiltersToLabels, DEFAULTS } = require('@/constants');
const { VSelect, VTextField, VSwitch, VCombobox } = require('vuetify/lib');

const { CUSTOM_PARAMETERS } = DEFAULTS.MATCHER;

export const ADVANCED_OPTIONS_GROUPS = {
  COMMON: 'COMMON',
  GENERAL: 'GENERAL',
  FILTERS: 'FILTERS',
  SYSTEM: 'SYSTEM'
};

export const orderedOptionsTabs = [
  ADVANCED_OPTIONS_GROUPS.COMMON,
  ADVANCED_OPTIONS_GROUPS.GENERAL,
  ADVANCED_OPTIONS_GROUPS.SYSTEM
];

const getVSwitchProps = (customProps = {}) => ({
  class: 'mt-0',
  'false-value': false,
  'true-value': true,
  ...customProps
});

export const commonOptions = [
  {
    key: 'filters',
    label: 'Category Filters',
    description:
      'The specific item categories the matcher should match against.',
    component: VSelect,
    props: {
      items: MatcherCategoryFiltersToLabels,
      'item-text': 'label',
      'item-value': 'value',
      multiple: true
    }
  },
  {
    key: 'defaultReferenceLab',
    label: 'External Lab Vendors',
    description:
      'The lab vendors used for external labs that the matcher should favor when matching against incoming lab items.',
    component: VSelect,
    props: {
      items: DEFAULTS.MATCHER.LABS,
      multiple: true
    }
  },
  {
    key: 'historicLab',
    label: 'Historic Lab Vendors',
    description:
      'Provides a list of labs that were previously used. The matcher will first try to match to the In-house or External lab vendors.',
    component: VSelect,
    props: {
      items: DEFAULTS.MATCHER.LABS,
      multiple: true
    }
  },
  {
    key: 'defaultlab',
    label: 'In-house Lab Vendors',
    description:
      'The lab vendors used for in-house labs that the matcher should favor when matching against incoming lab items.',
    component: VSelect,
    props: {
      items: DEFAULTS.MATCHER.LABS,
      multiple: true
    }
  },
  {
    key: 'labMatchingTechnique',
    label: 'Lab Matching Technique',
    description: `Drives lab matching behavior:
    - firm: requires stronger lab matches (e.g. ids, vendors, name, etc)
    - fuzzy: allows weaker lab matches
    `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.LAB_MATCHING_TECHNIQUE
    }
  },
  {
    key: 'locale',
    label: 'Locale',
    description:
      'Some matching logic is driven by specific locales (eg. en-US, es-ES)',
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.LOCALE_OPTIONS,
      multiple: true,
      clearable: true
    }
  },
  {
    key: 'unitSystem',
    label: 'Measurement System',
    description: 'The measurement system used by the source file.',
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.UNIT_SYSTEM
    }
  },
  {
    key: 'neversold',
    label: 'Never Sold Filter',
    description: `Drives the matcher's ignore behavior for never sold items:
  - off: Does not ignore items with no sales records
  - on: Ignores items with no sales records
  - price0: Only ignores items with no sales records if they have a price of 0
  `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.NEVER_SOLDS,
      clearable: true
    }
  }
];

export const advancedOptions = [
  {
    group: 'clinicname',
    label: 'Clinic Name',
    description: `- unique: Usually a unique, short, and convenient name that identifies a specific clinic and can be used by the matcher which may have specific clinic name mappings.
- official: Used in the html outputs to show the name of the practice. Intended to personalize the look of the html documents shown to clients.`,
    fields: [
      {
        key: 'clinic',
        label: 'Unique',
        component: VTextField,
        props: {
          readonly: true
        }
      },
      {
        key: 'officialname',
        label: 'Official ',
        component: VTextField
      }
    ]
  },
  {
    key: 'groupname',
    label: 'Group Name',
    description:
      'The group the associated clinic belongs to which may have specific matching logic.',
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.GROUPNAMES
    }
  },
  {
    group: 'Sales Drop',
    label: 'Sales Drop',
    description: `Type: If there were no sales in the last year, but the year before that there were greater than salesthreshold items sold, the item is ignored.
Threshold: The the minimum threshold in which which the sales drop type filter will starting ignoring items.`,
    fields: [
      {
        key: 'salesdrop',
        label: 'Type',
        component: VSelect,
        props: {
          items: CUSTOM_PARAMETERS.SALES_DROP,
          clearable: true
        }
      },
      {
        key: 'salesthreshold',
        label: 'Threshold',
        component: VTextField,
        props: {
          type: 'number'
        }
      }
    ]
  },
  {
    group: 'Cutoff',
    label: 'Cutoff',
    description: `A mode that dictates how the matcher decides to ignore a given item:
      - normal: take all values, no matter how long since they were last sold
      - firm: ignore if an item's last sold date is more than the specified cutoff months
      - threshold: similar to firm, but will also take the cutoff threshold into account
    `,
    fields: [
      {
        key: 'cutoff',
        label: 'Mode',
        component: VSelect,
        props: {
          items: CUSTOM_PARAMETERS.CUT_OFFS,
          clearable: true
        }
      },
      {
        key: 'cutoffmonths',
        label: 'Months',
        description:
          'The number of months since an item was last sold to be considered active. Only used if cutoff technique is set to firm or threshold.',
        component: VTextField,
        props: {
          type: 'number'
        }
      },
      {
        key: 'cutoffthreshold',
        label: 'Threshold',
        description:
          'The minimum unit quanity of an item sold to still be considered active. Used in addition to the cutoffmonths. Only used if cutoff technique is set to threshold.',
        component: VTextField,
        props: {
          type: 'number'
        }
      }
    ]
  },
  {
    key: 'labidmode',
    label: 'LabId Mode',
    description: `Modes that drive lab matching behavior:
  - all: Check all matching ids
  - normal: Attempts to find an overlap in the source and matched lab name to increase confidence
  - strong: The id should be unique enough that we're confident it's not an accidental match
  `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.LAB_ID_MODES,
      clearable: true
    }
  },
  {
    key: 'rxid',
    label: 'RxId Mode',
    description:
      'If the valude is set to idexx, the matcher will treat any lab with rx in it as an idexx lab',
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.RX_IDS
    }
  },
  {
    key: 'inhousefilter',
    label: 'In-house Lab Filter',
    description:
      'If set to “hint” and an in-house or reference indicator is found, the matcher will try to find labs that match that parameter. If no matching labs are found, it will return the unfiltered set. Useful when clinics put reference labs in an in-house category (e.g. the category is Idexx In House and the lab is only available as an idexx reference lab).',
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.IN_HOUSE_FILTERS
    }
  },
  {
    key: 'inhousesrc',
    label: 'In-house Source Lab Filter',
    description: `A hint to the matcher on which part of the lab data it should focus matching:
    - name: ignores any category associated with the source lab data
    - namewins: further narrows matching if the name contains a reference to an external lab or an in house name
    `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.IN_HOUSE_SRCS
    }
  },
  {
    key: 'vendorfilter',
    label: 'Vendor Filter',
    description: `Filters labs based upon vendors and will throw out labs that do not correspond to the vendor list:
    - normal: will try matching by any named vendors found in category or name. If none are found, assume the naming was correct filter out all labs. If there are no have named vendors it will try with the default vendors. If we are in fuzzy lab mode, it returns all labs. Otherwise, it does a last pass looking for internal lab codes. If no match, nothing is returned.
    - nameonly: if there are lab names, the matcher will only return labs that match those names. If there are none that match, it filters everything.
    - hint: uses the vendor names found in names and categories as hints and will return any found matches. This is useful for avimark hospitals that completely scramble their categories
  `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.VENDOR_FILTERS
    }
  },
  {
    key: 'categoryclassification',
    label: 'Category Classification',
    description: `Alters how item categories are classified:
    - standard: uses the same classification rules used for cornerstone and general pims matching, ignorning other PIMs specific classification rules (E.g. Useful for pet hospice which is a Vetter hospital, but has atypical Vetter categories)
    - null: adheres to specific PIMs rules based on incoming source data PIMs
  `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.CATEGORY_CLASSIFICATIONS
    }
  },
  {
    key: 'updatedsrcfile',
    label: 'Source File Type',
    description:
      'Tells the matcher the incoming file does not have the same set of lines as the previous run so it cannot rely on line ordering for internal logic.',
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.UPDATED_SRC_FILES
    }
  },
  {
    key: 'preservevariationnames',
    label: 'Preserve Variation Name',
    description:
      'Tells the matcher to use the existing variation name if a match from a previous run file is found.',
    component: VSwitch,
    props: getVSwitchProps({
      'false-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[0],
      'true-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[1]
    })
  },
  {
    key: 'preserveprices',
    label: 'Preserve Prices',
    descriptions:
      'Tells the matcher to use previousrun pricing info regardless of the ready for import status.',
    component: VSwitch,
    props: getVSwitchProps({
      'false-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[0],
      'true-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[1]
    })
  },
  {
    key: 'omitsizeproperty',
    label: 'Omit Size Property',
    description:
      'By default the matcher outputs size properties. The Rhapsody backend can fail with some of these units, so this leaves out the units in the output structure.',
    component: VSwitch,
    props: getVSwitchProps({
      'false-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[0],
      'true-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[1]
    })
  },
  {
    key: 'mergedispensingfees',
    label: 'Merge Dispensing Fees',
    description: `Tells the matcher how to handle incoming item dispensing fees:
    never: ignores any found dispensing fees
    always: always adds a found dispensing fee to the item price and sets the fee to 0
    clear labs and procedures: clears all lab and procedure dispensing fees
    merge labs and procedures: for labs and procedures, adds a found dispensing fee to the item price and sets the fee to 0
  `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.MERGE_DISPENSING_FEED_OPTIONS,
      clearable: true
    }
  },
  {
    key: 'clearminimumprice',
    label: 'Clear Minimum Price',
    description: 'Sets the minimum price of any entry to 0',
    component: VSwitch,
    props: getVSwitchProps({
      'false-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[0],
      'true-value': CUSTOM_PARAMETERS.OFF_ON_OPTIONS[1]
    })
  },
  {
    key: 'dontclearqoh',
    label: 'Do Not Clear QOH',
    description: `Drives matcher behavior when dealing with entry quanity on hand values:
  - normal: does not clear out negative QOH values
  - off: clears out negative QOH values
  `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.DONT_CLEAR_QOH_OPTIONS,
      clearable: true
    }
  },
  {
    key: 'ignorepimslabinfo',
    label: 'Ignore Pims Lab Info',
    description: `Tells the matcher to ignore additional lab metadata stored on the entry. Sometimes this is useful because the data might be stale and/or misleading.`,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.OFF_ON_OPTIONS
    }
  },
  {
    group: 'Diagnosis Word Match',
    label: 'Diagnosis Word Match',
    description: `Ratio: Minimum word match percentage required for source entry diagnosis to be considered a match.
    Max Multimatch: The maximum number of diagnosis multimatches before ignoring the entry altogether.`,
    fields: [
      {
        key: 'mindiagmatch',
        label: 'Ratio',
        component: VTextField,
        props: {
          type: 'number'
        }
      },
      {
        key: 'diagnosisignorecount',
        label: 'Max Multimatch',
        component: VTextField,
        props: {
          type: 'number'
        }
      }
    ]
  },
  {
    key: 'useprovidedvariationname',
    label: 'Use Provided Variation Name',
    description:
      'If the source entry has a provided variation name, use it instead of generating one.',
    component: VSwitch,
    props: getVSwitchProps()
  },
  {
    key: 'rematch',
    label: 'Rematch Ids',
    description:
      'The array of orig order (original line) values to be rematched. Only those lines will be processed.',
    component: VCombobox,
    props: {
      counter: true,
      clearable: true,
      'deletable-chips': true,
      'small-chips': true,
      multiple: true,
      'hide-selected': true
    }
  }
];

export const systemOptions = [
  {
    key: 'pass',
    label: 'Pass',
    description: 'Used by the special report to alter generated messages.',
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.PASSES,
      clearable: true
    }
  },
  {
    key: 'outputmode',
    label: 'Output Mode',
    description: `Filters the resulting mapped_core entries:
  - normal: persists all matches except for multimatches
  - upload: persists matches with review statuses of: yes, new, compount, asis, or supply and blank
  - yonly: persists matches with review statuses of: yes, compound, asis, and supply
  - all: persists all matches
  `,
    component: VSelect,
    props: {
      items: CUSTOM_PARAMETERS.OUTPUT_MODE,
      clearable: true
    }
  },
  {
    key: 'fullcore',
    label: 'Full Core Mode',
    description:
      'Tells the matcher we want to process every item in the source file. Sometimes, with a rematch for example, we do not want to reprocess every item.',
    component: VSwitch,
    props: getVSwitchProps()
  },
  {
    key: 'debug',
    label: 'Debug Mode',
    description:
      'If turned on, the matcher will log additional information about why an item did or did not match.',
    component: VSwitch,
    props: getVSwitchProps()
  }
];
